import React from 'react';
import { Drawer, Form, Button, SelectPicker, Message } from 'rsuite';
import { useTranslation } from 'react-i18next';

interface User {
  _id: string;
  firstname: string;
  lastname: string;
  registrationNumber: string;
}

interface Floor {
  _id: string;
  name: string;
}

interface Zone {
  _id: string;
  name: string;
  floor: Floor;
}

interface DrawerViewProps {
  open: boolean;
  onClose: () => void;
  onFiltersChange: (filters: any) => void;
  onResetFilters: () => void;
  currentFilters: {
    userId: string;
    status: string;
    floorId: string;
    zoneId: string;
    managerId: string;
  };
  users: User[];
  floors: Floor[];
  zones: Zone[];
  usersLoading: boolean;
  floorsLoading: boolean;
  zonesLoading: boolean;
  toaster: any;
}

const DrawerView: React.FC<DrawerViewProps> = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  users,
  floors,
  zones,
  usersLoading,
  floorsLoading,
  zonesLoading,
  toaster
}) => {
  const [formValue, setFormValue] = React.useState(currentFilters);

  const statusOptions = [
    { label: 'ALL', value: '' },
    { label: 'BOOKED', value: 'active' },
    { label: 'CHECKED-IN', value: 'ended' },
    { label: 'CHECKED-OUT', value: 'closed' },
    { label: 'CANCELLED', value: 'canceled' }
  ];

  const userOptions = React.useMemo(() => {
    return users?.map(user => ({
      label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
      value: user._id
    }));
  }, [users]);

  const managerOptions = React.useMemo(() => {
    return users.map(user => ({
      label: `${user.firstname} ${user.lastname}`,
      value: user._id
    }));
  }, [users]);

  const floorOptions = React.useMemo(() => {
    return [
      { label: 'ALL', value: '' },
      ...floors.map(floor => ({
        label: floor.name,
        value: floor._id
      }))
    ];
  }, [floors]);

  const zoneOptions = React.useMemo(() => {
    let filteredZones = zones;
    if (formValue.floorId) {
      filteredZones = zones.filter(zone => zone.floor._id === formValue.floorId);
    }

    return [
      { label: 'ALL', value: '' },
      ...filteredZones.map(zone => ({
        label: zone.name,
        value: zone._id
      }))
    ];
  }, [zones, formValue.floorId]);

  const handleSubmit = () => {
    const hasFilters = Object.values(formValue).some(value => value !== '' && value !== null);

    if (hasFilters) {
      onFiltersChange({
        userId: formValue.userId,
        status: formValue.status,
        floorId: formValue.floorId,
        zoneId: formValue.zoneId,
        managerId: formValue.managerId
      });
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Filters applied successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
    } else {
      onResetFilters();
    }
    onClose();
  };

  const handleReset = () => {
    setFormValue({
      userId: '',
      status: '',
      floorId: '',
      zoneId: '',
      managerId: ''
    });
    onResetFilters();
    toaster.push(
      <Message type="info" duration={3000}>
        {t('Filters have been reset!')}
      </Message>,
      { placement: 'topCenter' }
    );
    onClose();
  };

  React.useEffect(() => {
    if (open) {
      setFormValue(currentFilters);
    }
  }, [open, currentFilters]);

  // Reset zone when floor changes
  React.useEffect(() => {
    if (formValue.floorId === '') {
      setFormValue(prev => ({
        ...prev,
        zoneId: ''
      }));
    }
  }, [formValue.floorId]);
  const { t } = useTranslation();

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User & Registration Number')}</Form.ControlLabel>
            <Form.Control
              name="userId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t('Select user')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Manager')}</Form.ControlLabel>
            <Form.Control
              name="managerId"
              accepter={SelectPicker}
              data={managerOptions}
              searchable
              placeholder={t('Select manager')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Floor')}</Form.ControlLabel>
            <Form.Control
              name="floorId"
              accepter={SelectPicker}
              data={floorOptions}
              loading={floorsLoading}
              searchable
              placeholder={t('Select floor')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Zone</Form.ControlLabel>
            <Form.Control
              name="zoneId"
              accepter={SelectPicker}
              data={zoneOptions}
              loading={zonesLoading}
              searchable
              placeholder={t('Select zone')}
              disabled={!formValue.floorId}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Status')}</Form.ControlLabel>
            <Form.Control
              name="status"
              accepter={SelectPicker}
              data={statusOptions}
              searchable={false}
              placeholder={t('Select status')}
              block
            />
          </Form.Group>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;
